import { useCallback, useRef, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useGetCertificateByIdQuery } from "../../store/api/postgresCertificatesApi";
import html2canvas from "html2canvas";
import { useToast } from "../../context/CustomToast";

export const useCertificateDetailHook = () => {
  const { certificateId } = useParams<{ certificateId: string }>();
  const navigate = useNavigate();
  const certificateRef = useRef<HTMLDivElement>(null);
  const [showLinkedInModal, setShowLinkedInModal] = useState(false);
  const [pendingLinkedInUrl, setPendingLinkedInUrl] = useState("");
  const [pollWhilePending, setPollWhilePending] = useState(false);
  const { showToast } = useToast();
  // Fetch the specific certificate by ID
  const {
    data: certificate,
    isLoading,
    error,
  } = useGetCertificateByIdQuery(certificateId || "", {
    skip: !certificateId,
    pollingInterval: pollWhilePending ? 10000 : 0,
    skipPollingIfUnfocused: true,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    setPollWhilePending(certificate?.download_uri === null);
  }, [certificate?.download_uri]);

  const generateCertificateImage = async () => {
    if (!certificateRef.current) {return null;}
    const canvas = await html2canvas(certificateRef.current, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
    });
    return canvas.toDataURL("image/png");
  };

  const handleShareOnLinkedIn = async () => {
    // 1. Generate certificate image
    const imgData = await generateCertificateImage();
    if (!imgData) {return;}

    // 2. Trigger download for user
    const link = document.createElement("a");
    link.href = imgData;
    link.download = `${certificate?.course_name || "Certificate"}-Certificate.png`;
    link.click();

    // 3. Copy showcase text to clipboard
    const showcaseText = `I've just completed the "${certificate?.course_name || "Course"}" course on Pedagogue! 🎉\n\nCheck out my certificate below. #Pedagogue #Achievement #AI`;
    try {
      await navigator.clipboard.writeText(showcaseText);
    } catch (e) {
      // fallback: do nothing, user can copy manually if needed
    }

    // 4. Prepare LinkedIn share URL
    const shareUrl = encodeURIComponent(window.location.href);
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`;

    // Set URL first, then open modal
    setPendingLinkedInUrl(linkedInUrl);
    setTimeout(() => setShowLinkedInModal(true), 100);
  };

  const handleDownload = useCallback(() => {
    if (!certificate) {return;}
    if (!certificate.download_uri) {return;}
    window.open(certificate.download_uri, "_blank");
  }, [certificate]);

  const handleShare = useCallback(async () => {
    if (!certificate) {return;}

    if (navigator?.share) {
      try {
        await navigator.share({
          title: `${certificate.course_name} Certificate`,
          text: `I just completed ${certificate.course_name} and earned my certificate!`,
          url: window.location.href,
        });
      } catch (error) {
        console.error("Error sharing:", error);
      }
    } else {
      // Fallback for browsers that don't support native sharing
      try {
        await navigator.clipboard.writeText(window.location.href);
        showToast("Certificate link copied to clipboard!", "success");
      } catch (error) {
        console.error("Error copying link:", error);
        showToast("Failed to copy link to clipboard", "error");
      }
    }
  }, [certificate, showToast]);

  const handleBackToCertificates = useCallback(() => {
    navigate("/certificates");
  }, [navigate]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Redirect to certificate not found if certificate doesn't exist
  useEffect(() => {
    if (!isLoading && !certificate && certificateId && !error) {
      navigate("/certificates/not-found", { replace: true });
    }
  }, [certificate, certificateId, isLoading, navigate, error]);

  // Get error message based on error type
  const getErrorMessage = useCallback(() => {
    if (!error) {return null;}

    // Handle different error types
    if ("status" in error) {
      switch (error.status) {
      case 404:
        return "Certificate not found";
      case 400:
        return "Invalid certificate ID";
      case 500:
        return "Server error. Please try again later.";
      default:
        return "Failed to fetch certificate";
      }
    }

    return "Failed to fetch certificate";
  }, [error]);

  // Show loading state while fetching certificate
  if (isLoading) {
    return {
      certificate: null,
      certificateRef,
      handleDownload,
      handleShare,
      handleShareOnLinkedIn,
      handleBackToCertificates,
      isLoading: true,
      error: null,
      showLinkedInModal,
      setShowLinkedInModal,
      pendingLinkedInUrl,
    };
  }

  // Show error state
  if (error) {
    return {
      certificate: null,
      certificateRef,
      handleDownload,
      handleShare,
      handleShareOnLinkedIn,
      handleBackToCertificates,
      isLoading: false,
      error: getErrorMessage(),
      showLinkedInModal,
      setShowLinkedInModal,
      pendingLinkedInUrl,
    };
  }

  // If no certificate found, return null (will trigger redirect)
  if (!certificate) {
    return {
      certificate: null,
      certificateRef,
      handleDownload,
      handleShare,
      handleBackToCertificates,
      isLoading: false,
      error: null,
      showLinkedInModal,
      setShowLinkedInModal,
      pendingLinkedInUrl,
    };
  }

  return {
    certificate,
    certificateRef,
    handleDownload,
    handleShare,
    handleShareOnLinkedIn,
    handleBackToCertificates,
    isLoading: false,
    error: null,
    showLinkedInModal,
    setShowLinkedInModal,
    pendingLinkedInUrl,
  };
};
